import { Gauge, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/features/auth';
import { countryName, useSelectedCountry } from '@/features/country-scope';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatDateTime } from '@/shared/lib/format';

import { usePurchaseLimits } from '../api/purchase-limits.queries';
import { LimitStatusDialog } from '../components/limit-status-dialog';
import { PurchaseLimitDialog } from '../components/purchase-limit-dialog';
import { describeLimit } from '../lib/purchase-limit-labels';
import type { PurchaseLimit } from '../types/purchase-limit.types';

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; limit: PurchaseLimit }
  | { kind: 'status'; limit: PurchaseLimit }
  | null;

/**
 * Customer purchase limits (D-20) in the selected country: the limits on every
 * customer, and each customer's own. Checkout refuses a purchase that would go
 * above an active limit, before the customer pays, on every payment channel.
 * A customer's own limits are also managed from their page.
 */
export function PurchaseLimitsPage() {
  const { country } = useSelectedCountry();
  const { can } = usePermissions();
  const canManage = can('customers.limits.manage');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const limits = usePurchaseLimits(country?.id, includeInactive);
  const rows = limits.data ?? [];
  const countryWide = rows.filter((row) => row.customerId === null);
  const perCustomer = rows.filter((row) => row.customerId !== null);
  const close = () => setOpen(null);
  const name = country ? countryName(country) : 'this country';

  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/customers', label: 'Customers' }}
        title="Purchase limits"
        description={`How much, or how many orders, customers in ${name} may buy in a period. Checked at checkout before payment, on every payment channel.`}
        actions={
          canManage && country ? (
            <Button size="sm" onClick={() => setOpen({ kind: 'create' })}>
              <Plus className="size-3.5" aria-hidden />
              Limit for every customer
            </Button>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2 text-xs">
        <Checkbox
          id="limits-inactive"
          checked={includeInactive}
          onCheckedChange={(checked) => setIncludeInactive(checked === true)}
        />
        <Label htmlFor="limits-inactive" className="text-xs font-normal">
          Show switched-off limits
        </Label>
      </div>

      {limits.isLoading ? (
        <Skeleton className="h-48" />
      ) : limits.error ? (
        <ErrorState error={limits.error} onRetry={() => void limits.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Gauge}
          title="No purchase limits"
          description={
            includeInactive
              ? `Nobody in ${name} has a purchase limit, active or not.`
              : `No active limit in ${name}. Customers can buy without a limit.`
          }
        />
      ) : (
        <>
          <LimitsTable
            label="Every customer"
            rows={countryWide}
            canManage={canManage}
            onEdit={(limit) => setOpen({ kind: 'edit', limit })}
            onStatus={(limit) => setOpen({ kind: 'status', limit })}
          />
          <LimitsTable
            label="Individual customers"
            rows={perCustomer}
            canManage={canManage}
            onEdit={(limit) => setOpen({ kind: 'edit', limit })}
            onStatus={(limit) => setOpen({ kind: 'status', limit })}
          />
        </>
      )}

      {open?.kind === 'create' && country && (
        <PurchaseLimitDialog
          target={{ kind: 'country', countryId: country.id, countryName: name }}
          onClose={close}
        />
      )}
      {open?.kind === 'edit' && (
        <PurchaseLimitDialog
          target={
            open.limit.customerId === null
              ? { kind: 'country', countryId: open.limit.countryId, countryName: name }
              : {
                  kind: 'customer',
                  customerId: open.limit.customerId,
                  customerName: open.limit.customerName ?? `#${open.limit.customerId}`,
                }
          }
          limit={open.limit}
          onClose={close}
        />
      )}
      {open?.kind === 'status' && <LimitStatusDialog limit={open.limit} onClose={close} />}
    </PageContainer>
  );
}

function LimitsTable({
  label,
  rows,
  canManage,
  onEdit,
  onStatus,
}: {
  label: string;
  rows: PurchaseLimit[];
  canManage: boolean;
  onEdit: (limit: PurchaseLimit) => void;
  onStatus: (limit: PurchaseLimit) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{label}</h2>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">None.</p>
      ) : (
        <div className="border-border overflow-x-auto rounded-md border">
          <Table aria-label={label} className="text-xs">
            <TableHeader>
              <TableRow>
                {label !== 'Every customer' && <TableHead>Customer</TableHead>}
                <TableHead>Limit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last change</TableHead>
                {canManage && (
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((limit) => (
                <TableRow key={limit.id}>
                  {label !== 'Every customer' && (
                    <TableCell>
                      <Link
                        to={`/customers/${limit.customerId}`}
                        className="text-primary hover:underline"
                      >
                        {limit.customerName ?? `#${limit.customerId}`}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>{describeLimit(limit)}</TableCell>
                  <TableCell>
                    <StatusBadge variant={limit.isActive ? 'success' : 'neutral'}>
                      {limit.isActive ? 'Active' : 'Off'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(limit.updatedAtUtc ?? limit.createdAtUtc)}
                  </TableCell>
                  {canManage && (
                    <TableCell className="space-x-1 text-end">
                      {limit.isActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Edit limit ${describeLimit(limit)}`}
                          onClick={() => onEdit(limit)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`${limit.isActive ? 'Switch off' : 'Switch on'} limit ${describeLimit(limit)}`}
                        onClick={() => onStatus(limit)}
                      >
                        {limit.isActive ? 'Switch off' : 'Switch on'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
